import { useRender } from '@base-ui/react/use-render'
import { cva, type VariantProps } from 'class-variance-authority'

export const buttonVariants = cva(
  'ring-offset-background transitition focus-visible:outline-hidden has-data-[arrow=true]:justify-between group relative inline-flex cursor-pointer items-center justify-center whitespace-nowrap text-sm font-medium duration-200 disabled:pointer-events-none disabled:opacity-60 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary:
          'bg-primary text-primary-foreground hover:bg-primary/90 data-[state=open]:bg-primary/90',
        mono: 'bg-zinc-950 text-white hover:bg-zinc-950/90 data-[state=open]:bg-zinc-950/90 dark:bg-zinc-300 dark:text-black dark:hover:bg-zinc-300/90 dark:data-[state=open]:bg-zinc-300/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 data-[state=open]:bg-destructive/90 font-bold',
        'destructive-outline':
          'bg-destructive/1 text-destructive hover:bg-destructive/15 border-destructive data-[state=open]:bg-destructive/20 border font-bold',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/90 data-[state=open]:bg-secondary/90',
        outline:
          'bg-background text-accent-foreground border-input hover:bg-accent data-[state=open]:bg-accent border',
        dashed:
          'text-accent-foreground border-input bg-background hover:bg-accent hover:text-accent-foreground data-[state=open]:text-accent-foreground border border-dashed',
        ghost:
          'text-accent-foreground hover:bg-accent hover:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground',
        dim: 'text-muted-foreground hover:text-foreground data-[state=open]:text-foreground',
        foreground: '',
        inverse: '',
      },
      appearance: {
        default: '',
        ghost: '',
      },
      underline: {
        solid: '',
        dashed: '',
      },
      underlined: {
        solid: '',
        dashed: '',
      },
      size: {
        lg: 'h-10 gap-1.5 px-4 text-sm [&_svg:not([class*=size-])]:size-4',
        md: 'h-9 gap-1.5 px-3 text-sm [&_svg:not([class*=size-])]:size-4',
        sm: 'gap-1.25 h-8 px-2.5 text-xs [&_svg:not([class*=size-])]:size-3.5',
        xs: 'h-7 gap-1 px-2 text-xs [&_svg:not([class*=size-])]:size-3.5',
      },
      autoHeight: {
        true: '',
        false: '',
      },
      radius: {
        md: 'rounded-md',
        full: 'rounded-full',
      },
      mode: {
        default: 'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2',
        icon: 'focus-visible:ring-ring size-9 shrink-0 p-0 focus-visible:ring-2 focus-visible:ring-offset-2 [&_svg:not([class*=size-])]:size-5',
        link: 'text-primary h-auto rounded-none bg-transparent p-0 hover:bg-transparent data-[state=open]:bg-transparent',
        input: `hover:bg-background [&_svg]:hover:text-foreground data-[state=open]:bg-background focus-visible:border-ring focus-visible:ring-ring/30 [[data-state=open]>&]:border-ring [[data-state=open]>&]:ring-ring/30 aria-invalid:border-destructive/60 aria-invalid:ring-destructive/10 dark:aria-invalid:border-destructive dark:aria-invalid:ring-destructive/20 in-data-[invalid=true]:border-destructive/60 in-data-[invalid=true]:ring-destructive/10 dark:in-data-[invalid=true]:border-destructive dark:in-data-[invalid=true]:ring-destructive/20 focus-visible:outline-hidden [[data-state=open]>&]:outline-hidden justify-start font-normal focus-visible:ring-[3px] [&_svg]:transition-colors [[data-state=open]>&]:ring-[3px]`,
      },
      placeholder: {
        true: 'text-muted-foreground',
        false: '',
      },
    },
    compoundVariants: [
      // Icons opacity for default mode
      {
        variant: 'ghost',
        mode: 'default',
        className: '[&_svg:not([role=img]):not([class*=text-]):not([class*=opacity-])]:opacity-60',
      },
      {
        variant: 'outline',
        mode: 'default',
        className: '[&_svg:not([role=img]):not([class*=text-]):not([class*=opacity-])]:opacity-60',
      },
      {
        variant: 'dashed',
        mode: 'default',
        className: '[&_svg:not([role=img]):not([class*=text-]):not([class*=opacity-])]:opacity-60',
      },
      {
        variant: 'secondary',
        mode: 'default',
        className: '[&_svg:not([role=img]):not([class*=text-]):not([class*=opacity-])]:opacity-60',
      },

      // Icons opacity for default mode
      {
        variant: 'outline',
        mode: 'input',
        className: '[&_svg:not([role=img]):not([class*=text-]):not([class*=opacity-])]:opacity-60',
      },
      {
        variant: 'outline',
        mode: 'icon',
        className: '[&_svg:not([role=img]):not([class*=text-]):not([class*=opacity-])]:opacity-60',
      },

      // Auto height
      {
        size: 'xs',
        autoHeight: true,
        className: 'h-auto min-h-7',
      },
      {
        size: 'md',
        autoHeight: true,
        className: 'h-auto min-h-9',
      },
      {
        size: 'sm',
        autoHeight: true,
        className: 'h-auto min-h-8',
      },
      {
        size: 'lg',
        autoHeight: true,
        className: 'h-auto min-h-10',
      },

      // Shadow support
      {
        variant: 'primary',
        mode: 'default',
        appearance: 'default',
        className: 'shadow-xs shadow-black/5',
      },
      {
        variant: 'mono',
        mode: 'default',
        appearance: 'default',
        className: 'shadow-xs shadow-black/5',
      },
      {
        variant: 'secondary',
        mode: 'default',
        appearance: 'default',
        className: 'shadow-xs shadow-black/5',
      },
      {
        variant: 'outline',
        mode: 'default',
        appearance: 'default',
        className: 'shadow-xs shadow-black/5',
      },
      {
        variant: 'dashed',
        mode: 'default',
        appearance: 'default',
        className: 'shadow-xs shadow-black/5',
      },
      {
        variant: 'destructive',
        mode: 'default',
        appearance: 'default',
        className: 'shadow-xs shadow-black/5',
      },

      // Shadow support
      {
        variant: 'primary',
        mode: 'icon',
        appearance: 'default',
        className: 'shadow-xs shadow-black/5',
      },
      {
        variant: 'mono',
        mode: 'icon',
        appearance: 'default',
        className: 'shadow-xs shadow-black/5',
      },
      {
        variant: 'secondary',
        mode: 'icon',
        appearance: 'default',
        className: 'shadow-xs shadow-black/5',
      },
      {
        variant: 'outline',
        mode: 'icon',
        appearance: 'default',
        className: 'shadow-xs shadow-black/5',
      },
      {
        variant: 'dashed',
        mode: 'icon',
        appearance: 'default',
        className: 'shadow-xs shadow-black/5',
      },
      {
        variant: 'destructive',
        mode: 'icon',
        appearance: 'default',
        className: 'shadow-xs shadow-black/5',
      },

      // Link
      {
        variant: 'primary',
        mode: 'link',
        underline: 'solid',
        className:
          'text-primary hover:text-primary/90 font-medium hover:underline hover:decoration-solid hover:underline-offset-4 [&_svg:not([role=img]):not([class*=text-])]:opacity-60',
      },
      {
        variant: 'primary',
        mode: 'link',
        underline: 'dashed',
        className:
          'text-primary hover:text-primary/90 font-medium decoration-1 hover:underline hover:decoration-dashed hover:underline-offset-4 [&_svg]:opacity-60',
      },
      {
        variant: 'primary',
        mode: 'link',
        underlined: 'solid',
        className:
          'text-primary hover:text-primary/90 font-medium underline decoration-solid underline-offset-4 [&_svg:not([role=img]):not([class*=text-])]:opacity-60',
      },
      {
        variant: 'primary',
        mode: 'link',
        underlined: 'dashed',
        className:
          'text-primary hover:text-primary/90 font-medium underline decoration-dashed decoration-1 underline-offset-4 [&_svg]:opacity-60',
      },

      {
        variant: 'inverse',
        mode: 'link',
        underline: 'solid',
        className:
          'font-medium text-inherit hover:underline hover:decoration-solid hover:underline-offset-4 [&_svg:not([role=img]):not([class*=text-])]:opacity-60',
      },
      {
        variant: 'inverse',
        mode: 'link',
        underline: 'dashed',
        className:
          'font-medium text-inherit decoration-1 hover:underline hover:decoration-dashed hover:underline-offset-4 [&_svg:not([role=img]):not([class*=text-])]:opacity-60',
      },
      {
        variant: 'inverse',
        mode: 'link',
        underlined: 'solid',
        className:
          'font-medium text-inherit underline decoration-solid underline-offset-4 [&_svg:not([role=img]):not([class*=text-])]:opacity-60',
      },
      {
        variant: 'inverse',
        mode: 'link',
        underlined: 'dashed',
        className:
          'font-medium text-inherit underline decoration-dashed decoration-1 underline-offset-4 [&_svg:not([role=img]):not([class*=text-])]:opacity-60',
      },

      {
        variant: 'foreground',
        mode: 'link',
        underline: 'solid',
        className:
          'text-foreground font-medium hover:underline hover:decoration-solid hover:underline-offset-4 [&_svg:not([role=img]):not([class*=text-])]:opacity-60',
      },
      {
        variant: 'foreground',
        mode: 'link',
        underline: 'dashed',
        className:
          'text-foreground font-medium decoration-1 hover:underline hover:decoration-dashed hover:underline-offset-4 [&_svg:not([role=img]):not([class*=text-])]:opacity-60',
      },
      {
        variant: 'foreground',
        mode: 'link',
        underlined: 'solid',
        className:
          'text-foreground font-medium underline decoration-solid underline-offset-4 [&_svg:not([role=img]):not([class*=text-])]:opacity-60',
      },
      {
        variant: 'foreground',
        mode: 'link',
        underlined: 'dashed',
        className:
          'text-foreground font-medium underline decoration-dashed decoration-1 underline-offset-4 [&_svg:not([role=img]):not([class*=text-])]:opacity-60',
      },

      // Ghost
      {
        variant: 'primary',
        appearance: 'ghost',
        className:
          'text-primary/90 hover:bg-primary/5 data-[state=open]:bg-primary/5 bg-transparent',
      },
      {
        variant: 'destructive',
        appearance: 'ghost',
        className:
          'text-destructive/90 hover:bg-destructive/5 data-[state=open]:bg-destructive/5 bg-transparent',
      },
      {
        variant: 'ghost',
        mode: 'icon',
        className: 'text-muted-foreground',
      },

      // Icon sizes
      {
        mode: 'icon',
        size: 'xs',
        className: 'size-7 shrink-0 p-0 [&_svg:not([class*=size-])]:size-3.5',
      },
      {
        mode: 'icon',
        size: 'sm',
        className: 'size-8 shrink-0 p-0 [&_svg:not([class*=size-])]:size-4',
      },
      {
        mode: 'icon',
        size: 'md',
        className: 'size-9 shrink-0 p-0 [&_svg:not([class*=size-])]:size-5',
      },
      {
        mode: 'icon',
        size: 'lg',
        className: 'size-10 shrink-0 p-0 [&_svg:not([class*=size-])]:size-6',
      },

      // Input mode
      {
        mode: 'input',
        placeholder: true,
        variant: 'outline',
        className: 'text-muted-foreground font-normal',
      },
      {
        mode: 'input',
        variant: 'outline',
        size: 'sm',
        className: 'gap-1.25',
      },
      {
        mode: 'input',
        variant: 'outline',
        size: 'md',
        className: 'gap-1.5',
      },
      {
        mode: 'input',
        variant: 'outline',
        size: 'lg',
        className: 'gap-1.5',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      mode: 'default',
      size: 'md',
      radius: 'md',
      appearance: 'default',
    },
  }
)

export type ButtonVariantProps = VariantProps<typeof buttonVariants>

export interface ButtonProps extends useRender.ComponentProps<'button'>, ButtonVariantProps {
  isLoading?: boolean
  loadingText?: string
  selected?: boolean
}
